import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './AddPatient.css';
import Layout from '../components/Layout';
import { intakeFormService } from '../services/intakeFormService';
import { domainService } from '../services/domainService';

const AddPatient = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // 1: Form, 2: Prompt
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domains, setDomains] = useState([]);
  const [isLoadingDomains, setIsLoadingDomains] = useState(true);
  const [customFields, setCustomFields] = useState([]);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [prompt, setPrompt] = useState('');
  const fileInputRef = useRef(null);
  const [newField, setNewField] = useState({
    type: 'text',
    title: '',
    id: '',
    placeholder: ''
  });
  const [formData, setFormData] = useState({
    // Identifying Information
    fullName: '',
    dob: '',
    age: '',
    phoneNumber: '',
    emergencyContact: '',
    email: '',
    primaryLanguage: '',
    raceEthnicity: '',
    gender: '',
    bloodGroup: '',
    address: '',

    // Address Information
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',

    // Referral & Presenting Concerns
    referralSource: '',
    durationOfSymptoms: '',
    currentStressors: '',
    visitMotivation: '',
    chiefComplaint: '',

    // Medical & Psychiatric History
    currentMedications: '',
    hospitalizationHistory: '',
    pastDiagnoses: '',
    headInjuryHistory: '',

    // Family & Social Background
    familyMentalHealth: '',
    maritalStatus: '',
    livingSituation: '',
    employment: '',
    supportSystemStrength: '',

    // Substance Use & Risk Behaviors
    substanceUse: {
      alcohol: { frequency: '', duration: '', lastUse: '', relatedIssues: '' },
      cannabis: { frequency: '', duration: '', lastUse: '', relatedIssues: '' },
      prescription: { frequency: '', duration: '', lastUse: '', relatedIssues: '' },
      misuse: { frequency: '', duration: '', lastUse: '', relatedIssues: '' },
      others: { frequency: '', duration: '', lastUse: '', relatedIssues: '' }
    },

    // Symptom Tracking (Quantitative)
    symptomTracking: {
      anxiety: { rating: '', onset: '', frequency: '', notes: '' },
      depression: { rating: '', onset: '', frequency: '', notes: '' },
      attention: { rating: '', onset: '', frequency: '', notes: '' },
      impulsivity: { rating: '', onset: '', frequency: '', notes: '' },
      sleepProblems: { rating: '', onset: '', frequency: '', notes: '' },
      irritability: { rating: '', onset: '', frequency: '', notes: '' },
      suicidalIdeation: { rating: '', onset: '', frequency: '', notes: '' }
    },

    // Functional Impact
    dailyFunctioning: '',
    workSchoolPerformance: '',
    socialRelationships: '',
    selfCareMotivation: '',

    // Mental Status Exam
    mentalStatusExam: {
      appearance: '',
      speech: '',
      affect: '',
      thoughtProcess: '',
      thoughtContent: '',
      insightJudgment: '',
      orientation: ''
    },

    // Research Consent
    dataUseConsent: 'yes',
    analyticsConsent: 'yes',

    // Clinician Summary
    preliminaryImpression: '',
    nextSteps: '',
    followUpPlan: ''
  });

  // Fetch domains from API on component mount
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        setIsLoadingDomains(true);
        const data = await domainService.getDomains();
        setDomains(data);
      } catch (error) {
        console.error('Error fetching domains:', error);
        setSaveError('Failed to load domains. Please refresh the page.');
      } finally {
        setIsLoadingDomains(false);
      }
    };

    fetchDomains();
  }, []);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  // Handler for nested substance use fields
  const handleSubstanceUseChange = (substance, field, value) => {
    setFormData(prev => ({
      ...prev,
      substanceUse: {
        ...prev.substanceUse,
        [substance]: {
          ...prev.substanceUse[substance],
          [field]: value
        }
      }
    }));
  };

  // Handler for nested symptom tracking fields
  const handleSymptomTrackingChange = (symptom, field, value) => {
    setFormData(prev => ({
      ...prev,
      symptomTracking: {
        ...prev.symptomTracking,
        [symptom]: {
          ...prev.symptomTracking[symptom],
          [field]: value
        }
      }
    }));
  };

  // Handler for nested mental status exam fields
  const handleMentalStatusExamChange = (domain, value) => {
    setFormData(prev => ({
      ...prev,
      mentalStatusExam: {
        ...prev.mentalStatusExam,
        [domain]: value
      }
    }));
  };

  const handleAddFieldClick = () => {
    setShowFieldModal(true);
  };

  const handleFieldModalChange = (e) => {
    const { name, value } = e.target;
    setNewField(prev => ({ ...prev, [name]: value }));
  };

  const handleAddField = () => {
    if (newField.title && newField.id) {
      setCustomFields(prev => [...prev, { ...newField }]);
      setFormData(prev => ({ ...prev, [newField.id]: '' }));
      setNewField({ type: 'text', title: '', id: '', placeholder: '' });
      setShowFieldModal(false);
    }
  };

  const handleRemoveField = (fieldId) => {
    setCustomFields(prev => prev.filter(field => field.id !== fieldId));
    setFormData(prev => {
      const newData = { ...prev };
      delete newData[fieldId];
      return newData;
    });
  };

  const handleFormSubmit = (e) => {
    // Prevent any form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // This should ONLY navigate, NEVER save
    handleNextStep();
    return false;
  };

  const handleNextStep = () => {
    // Validate required fields before moving to next step
    if (!formData.fullName) {
      setSaveError('Please enter the patient\'s full name');
      return;
    }

    // Load domain-specific prompt if available
    if (selectedDomain && selectedDomain.prompt) {
      setPrompt(selectedDomain.prompt);
    }

    setSaveError('');
    setCurrentStep(2);
  };

  const handleBackStep = () => {
    setCurrentStep(1);
  };

  const handleSaveToTemplates = () => {
    // TODO: Implement save to templates functionality
    alert('Prompt saved to templates!');
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Safety check: only save on step 2
    if (currentStep !== 2) {
      alert('Please complete the prompt section first');
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      // Prepare intake form data
      const intakeFormData = {
        ...formData,
        selectedDomain: selectedDomain ? {
          id: selectedDomain._id,
          name: selectedDomain.name
        } : null,
        customFields: customFields.reduce((acc, field) => {
          acc[field.id] = formData[field.id] || '';
          return acc;
        }, {}),
        prompt: prompt, // Add the prompt to intake form data
        status: 'draft' // Can be 'draft' or 'completed'
      };

      // Call the intake form service to create the intake form
      const response = await intakeFormService.createIntakeForm(intakeFormData);

      // Show success message
      alert('Intake form saved successfully!');

      // Navigate to intake forms list
      navigate('/intake-forms');

    } catch (error) {
      console.error('Error saving intake form:', error);
      setSaveError(error.response?.data?.message || error.message || 'Failed to save intake form. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // File upload handlers
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Please upload a PDF or DOC/DOCX file');
      return;
    }

    // Validate file size (100MB)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('File size must be less than 100MB');
      return;
    }

    setIsUploading(true);
    setUploadError('');
    setUploadedFile(file);

    try {
      // Use the intakeFormService method to properly handle API URL
      const result = await intakeFormService.uploadPdfAndCreateIntakeForm(file);
      console.log('Upload response:', result);

      // Check if extraction was successful
      if (result.success && result.data) {
        // The backend now returns structured data, not raw text
        // If you need to extract text, you can use result.data.text
        // For now, we'll skip the parseAndMapData since the backend handles this
        console.log('File uploaded successfully:', result.data);
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Failed to upload and process file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const parseAndMapData = (text) => {
    // Helper function to extract value after a label
    const extractValue = (label) => {
      const regex = new RegExp(`${label}:\\s*(.*)`, 'i');
      const match = text.match(regex);
      return match ? match[1].trim() : '';
    };

    // Extract and map data from the PDF text
    const parsedData = {
      // Identifying Information
      fullName: extractValue('Full Name'),
      dob: extractValue('Date of Birth'),
      age: extractValue('Age'),
      gender: extractValue('Gender'),
      raceEthnicity: extractValue('Race/Ethnicity'),
      primaryLanguage: extractValue('Primary Language'),
      address: extractValue('Address'),
      phoneNumber: extractValue('Phone'),
      email: extractValue('Email'),
      emergencyContact: extractValue('Emergency Contact'),

      // Referral & Presenting Concerns
      referralSource: extractValue('Referral Source'),
      chiefComplaint: extractValue('Chief Complaint'),
      durationOfSymptoms: extractValue('Duration of symptoms'),
      currentStressors: extractValue('Current stressors'),
      visitMotivation: extractValue('Motivation'),

      // Medical & Psychiatric History
      currentMedications: extractValue('Current Medications'),
      pastDiagnoses: extractValue('Past psychiatric diagnoses'),
      hospitalizationHistory: extractValue('Hospitalizations'),

      // Family & Social Background
      familyMentalHealth: extractValue('Family Mental Health History'),
      maritalStatus: extractValue('Marital Status'),
      livingSituation: extractValue('Living Situation'),
      employment: extractValue('Employment'),
      supportSystemStrength: extractValue('Support System Strength'),

      // Functional Impact
      dailyFunctioning: extractValue('Daily functioning'),
      workSchoolPerformance: extractValue('Work performance'),
      socialRelationships: extractValue('Social Relationships'),
      selfCareMotivation: extractValue('Self-Care / Motivation'),

      // Research Consent
      dataUseConsent: extractValue('Consent to anonymized data use for AI & research module')?.toLowerCase() === 'yes' ? 'yes' : 'no',
      analyticsConsent: extractValue('Consent for behavioral analytics tracking')?.toLowerCase() === 'yes' ? 'yes' : 'no',

      // Clinician Summary
      preliminaryImpression: extractValue('Preliminary Impression'),
      nextSteps: extractValue('Next Steps'),
      followUpPlan: extractValue('Follow-Up Plan'),
    };

    // Update form data with parsed values
    setFormData(prev => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(parsedData).filter(([_, value]) => value !== '')
      )
    }));
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['link', 'image'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'link', 'image',
    'list', 'bullet'
  ];

  return (
    <Layout>
      <div className="p-2.5 bg-white rounded-lg border border-[#C4CADA] min-h-screen">
        <div className="max-w-[1189px] mx-auto bg-white flex flex-col items-center gap-3">

          {/* Title */}
          <div className="w-full text-center text-[#001C3B] text-2xl font-bold font-['Poppins'] leading-10">
            {currentStep === 1 ? 'Initial Assessment Report' : `${selectedDomain?.name || 'Clinical Psychology'} Prompt`}
          </div>

          {currentStep === 1 ? (
            // STEP 1: Form Section
            <form onSubmit={handleFormSubmit} className="w-full flex flex-col items-center gap-3">

          {/* Select Domain Section */}
          <div className="w-full bg-white rounded-lg border border-[#C4CADA] flex flex-col justify-center items-center p-5">
            <div className="w-full text-[#111827] text-base font-semibold font-['Poppins'] leading-10 mb-3">
              Select Domain
            </div>
            {isLoadingDomains ? (
              <div className="w-full flex justify-center items-center py-4">
                <div className="text-[#6B7280] text-sm font-normal font-['Poppins']">
                  Loading domains...
                </div>
              </div>
            ) : (
              <div className="w-full flex justify-center items-center gap-6 flex-wrap">
                {domains.map((domain) => {
                  const isForensic = domain.name === 'Forensic Psychological';
                  const isDisabled = isForensic;
                  
                  return (
                    <div key={domain._id} className="flex flex-col items-center gap-1">
                      <button
                        onClick={() => !isDisabled && setSelectedDomain(domain)}
                        disabled={isDisabled}
                        className={`px-7 py-1 rounded-md border border-[#001C3B] text-sm font-medium font-['Poppins'] leading-7 transition-colors ${
                          isDisabled
                            ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                            : selectedDomain?._id === domain._id
                            ? 'bg-blue-100 text-[#001C3B]'
                            : 'bg-white text-[#001C3B] hover:bg-blue-50'
                        }`}
                      >
                        {domain.name}
                      </button>
                      {isForensic && (
                        <span className="text-xs text-gray-500 font-normal font-['Poppins']">
                          Coming soon
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* File Upload Section */}
          <div
            className={`w-full p-[34px] bg-white rounded-2xl border-2 flex flex-col justify-center items-center gap-4 cursor-pointer transition-colors ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-[#424242] hover:border-blue-400'
            } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClickUpload}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileInputChange}
              className="hidden"
              disabled={isUploading}
            />

            {isUploading ? (
              <>
                <div className="text-center text-blue-500 text-[60px] font-black leading-[60px]">
                  ⏳
                </div>
                <div className="text-center text-blue-600 text-xl font-bold font-['Poppins'] leading-7">
                  Processing file...
                </div>
              </>
            ) : uploadedFile ? (
              <>
                <div className="text-center text-green-500 text-[60px] font-black leading-[60px]">
                  ✓
                </div>
                <div className="text-center text-[#001C3B] text-xl font-bold font-['Poppins'] leading-7">
                  {uploadedFile.name}
                </div>
                <div className="text-center text-green-600 text-sm font-normal font-['Poppins'] leading-5">
                  File uploaded and data extracted successfully!
                </div>
              </>
            ) : (
              <>
                <div className="text-center text-[#424242] text-[60px] font-black font-['Font Awesome 5 Free'] leading-[60px]">
                  📁
                </div>
                <div className="text-center text-[#001C3B] text-xl font-bold font-['Poppins'] leading-7">
                  Drag and drop files here
                </div>
                <div className="flex items-start gap-0">
                  <div className="text-center text-[#001C3B] text-xl font-semibold font-['Inter'] leading-7">Or </div>
                  <div className="text-center text-[#001C3B] text-xl font-semibold font-['Poppins'] leading-7">
                    click to select files
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="text-center text-[#6B7280] text-sm font-normal font-['Poppins'] leading-5">
                    Supported: PDF, DOC, DOCX, HEIC, JPEG, PNG, TXT
                  </div>
                  <div className="text-center text-[#6B7280] text-sm font-normal font-['Poppins'] leading-5">
                    Max file size: 100MB
                  </div>
                </div>
              </>
            )}

            {uploadError && (
              <div className="text-center text-red-500 text-sm font-medium font-['Poppins'] leading-5">
                {uploadError}
              </div>
            )}
          </div>

          {/* Identifying Information */}
          <div className="w-full p-5 bg-white rounded-lg border border-[#C4CADA] flex flex-col gap-5">
            <div className="text-[#111827] text-base font-semibold font-['Poppins'] capitalize leading-10">
              Identifying Information
            </div>

            <div className="flex flex-col gap-4">
              {/* Row 1 */}
              <div className="flex gap-4">
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Full Name <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    DOB <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-['Poppins']"
                  />
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Age <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div className="flex gap-4">
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Phone Number <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="000-000-0000"
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Emergency Contact <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="tel"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleInputChange}
                    placeholder="000-000-0000"
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address (Optional)"
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Row 3 */}
              <div className="flex gap-4">
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Primary Language <span className="text-[#FF0000]">*</span>
                  </label>
                  <select
                    name="primaryLanguage"
                    value={formData.primaryLanguage}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-['Poppins']"
                  >
                    <option value="">Select</option>
                    <option value="English">English</option>
                    <option value="Spanish">Spanish</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Race/Ethnicity <span className="text-[#FF0000]">*</span>
                  </label>
                  <select
                    name="raceEthnicity"
                    value={formData.raceEthnicity}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-['Poppins']"
                  >
                    <option value="">Select</option>
                    <option value="Asian">Asian</option>
                    <option value="Black">Black</option>
                    <option value="Hispanic">Hispanic</option>
                    <option value="White">White</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Row 4 */}
              <div className="flex gap-4">
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Gender <span className="text-[#FF0000]">*</span>
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-['Poppins']"
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Blood Group <span className="text-[#FF0000]">*</span>
                  </label>
                  <select
                    name="bloodGroup"
                    value={formData.bloodGroup}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-['Poppins']"
                  >
                    <option value="">Select</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
              </div>

            </div>
          </div>

          {/* Address Information - US Only */}
          <div className="w-full p-5 bg-white rounded-lg border border-[#C4CADA] flex flex-col gap-5">
            <div className="text-[#111827] text-base font-semibold font-['Poppins'] capitalize leading-10">
              Address Information
            </div>

            <div className="flex flex-col gap-4">
              {/* Street Address */}
              <div className="flex flex-col gap-1">
                <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                  Street Address <span className="text-[#FF0000]">*</span>
                </label>
                <input
                  type="text"
                  name="streetAddress"
                  value={formData.streetAddress}
                  onChange={handleInputChange}
                  placeholder="Enter street address"
                  className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* City, State, ZIP Code */}
              <div className="flex gap-4">
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    City <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Enter city"
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    State <span className="text-[#FF0000]">*</span>
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-['Poppins']"
                  >
                    <option value="">Select State</option>
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option>
                    <option value="CA">California</option>
                    <option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option>
                    <option value="DE">Delaware</option>
                    <option value="FL">Florida</option>
                    <option value="GA">Georgia</option>
                    <option value="HI">Hawaii</option>
                    <option value="ID">Idaho</option>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="IA">Iowa</option>
                    <option value="KS">Kansas</option>
                    <option value="KY">Kentucky</option>
                    <option value="LA">Louisiana</option>
                    <option value="ME">Maine</option>
                    <option value="MD">Maryland</option>
                    <option value="MA">Massachusetts</option>
                    <option value="MI">Michigan</option>
                    <option value="MN">Minnesota</option>
                    <option value="MS">Mississippi</option>
                    <option value="MO">Missouri</option>
                    <option value="MT">Montana</option>
                    <option value="NE">Nebraska</option>
                    <option value="NV">Nevada</option>
                    <option value="NH">New Hampshire</option>
                    <option value="NJ">New Jersey</option>
                    <option value="NM">New Mexico</option>
                    <option value="NY">New York</option>
                    <option value="NC">North Carolina</option>
                    <option value="ND">North Dakota</option>
                    <option value="OH">Ohio</option>
                    <option value="OK">Oklahoma</option>
                    <option value="OR">Oregon</option>
                    <option value="PA">Pennsylvania</option>
                    <option value="RI">Rhode Island</option>
                    <option value="SC">South Carolina</option>
                    <option value="SD">South Dakota</option>
                    <option value="TN">Tennessee</option>
                    <option value="TX">Texas</option>
                    <option value="UT">Utah</option>
                    <option value="VT">Vermont</option>
                    <option value="VA">Virginia</option>
                    <option value="WA">Washington</option>
                    <option value="WV">West Virginia</option>
                    <option value="WI">Wisconsin</option>
                    <option value="WY">Wyoming</option>
                  </select>
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    ZIP Code <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    placeholder="00000"
                    maxLength="5"
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Country */}
              <div className="flex flex-col gap-1">
                <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                  Country <span className="text-[#FF0000]">*</span>
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-['Poppins']"
                >
                  <option value="United States">United States</option>
                </select>
              </div>
            </div>
          </div>

          {/* Referral & Presenting Concerns */}
          <div className="w-full p-5 bg-white rounded-lg border border-[#C4CADA] flex flex-col gap-5">
            <div className="text-[#111827] text-base font-semibold font-['Poppins'] capitalize leading-10">
              Referral & Presenting Concerns
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex gap-4">
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Referral Source <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="text"
                    name="referralSource"
                    value={formData.referralSource}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Duration of symptoms: <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="text"
                    name="durationOfSymptoms"
                    value={formData.durationOfSymptoms}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                  Current Stressors/Triggers <span className="text-[#FF0000]">*</span>
                </label>
                <input
                  type="text"
                  name="currentStressors"
                  value={formData.currentStressors}
                  onChange={handleInputChange}
                  className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                  What Motivated This Visit Today? <span className="text-[#FF0000]">*</span>
                </label>
                <input
                  type="text"
                  name="visitMotivation"
                  value={formData.visitMotivation}
                  onChange={handleInputChange}
                  className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                  Chief Complaint (in client's words) <span className="text-[#FF0000]">*</span>
                </label>
                <textarea
                  name="chiefComplaint"
                  value={formData.chiefComplaint}
                  onChange={handleInputChange}
                  rows="5"
                  className="px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>
          </div>

       

          {/* Medical & Psychiatric History */}
          <div className="self-stretch p-5 bg-white rounded-lg outline outline-1 outline-offset-[-0.50px] outline-slate-300 inline-flex flex-col justify-start items-start gap-5">
            <div className="self-stretch justify-center text-gray-900 text-base font-semibold font-['Poppins'] capitalize leading-10 line-clamp-1">Medical & Psychiatric History</div>
            <div data-property-1="Input Default" className="self-stretch flex flex-col justify-start items-start gap-1">
                <div className="self-stretch justify-start"><span className="text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">Current Medications (list names, dosage, duration)</span><span className="text-red-600 text-sm font-medium font-['Poppins'] leading-6 tracking-wide"> *</span></div>
                <textarea
                  name="currentMedications"
                  value={formData.currentMedications}
                  onChange={handleInputChange}
                  rows="5"
                  className="self-stretch px-3 py-1.5 bg-white rounded-md outline outline-1 outline-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter current medications..."
                />
            </div>
            <div className="self-stretch inline-flex justify-start items-start gap-3">
                <div data-property-1="Input Default" className="w-[585px] self-stretch inline-flex flex-col justify-start items-start gap-1">
                    <div className="self-stretch justify-start"><span className="text-teal-950 text-sm font-medium font-['Poppins'] leading-5">Hospitalizations or ER visits (yes/no; if yes, explain) </span><span className="text-red-600 text-sm font-medium font-['Poppins'] leading-5">*</span></div>
                    <textarea
                      name="hospitalizationHistory"
                      value={formData.hospitalizationHistory}
                      onChange={handleInputChange}
                      rows="3"
                      className="self-stretch px-3 py-1.5 bg-white rounded-md outline outline-1 outline-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Yes/No and details..."
                    />
                </div>
                <div className="flex-1 inline-flex flex-col justify-start items-start gap-4">
                    <div className="self-stretch inline-flex justify-start items-start gap-4">
                        <div data-property-1="Input Default" className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                            <div className="self-stretch justify-start"><span className="text-teal-950 text-sm font-medium font-['Poppins'] leading-5">Past psychiatric diagnoses</span><span className="text-red-600 text-sm font-medium font-['Poppins'] leading-5"> *</span></div>
                            <input
                              type="text"
                              name="pastDiagnoses"
                              value={formData.pastDiagnoses}
                              onChange={handleInputChange}
                              className="self-stretch h-9 px-3 py-1.5 bg-white rounded-md outline outline-1 outline-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Enter past diagnoses..."
                            />
                        </div>
                    </div>
                    <div className="self-stretch inline-flex justify-center items-end gap-4">
                        <div data-property-1="Input Default" className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                            <div className="self-stretch justify-start"><span className="text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">History of head injury, seizures, or chronic illness </span><span className="text-red-600 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">*</span></div>
                            <input
                              type="text"
                              name="headInjuryHistory"
                              value={formData.headInjuryHistory}
                              onChange={handleInputChange}
                              className="self-stretch h-9 px-3 py-1.5 bg-white rounded-md outline outline-1 outline-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Enter history..."
                            />
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Family & Social Background */}
          <div className="w-[1188px] rounded-lg outline outline-1 outline-offset-[-1px] outline-slate-300 inline-flex flex-col justify-center items-center gap-2.5">
            <div className="w-[1164px] flex flex-col justify-start items-start gap-2.5 p-5">
                <div className="w-[1164px] flex flex-col justify-start items-start gap-3">
                    <div className="self-stretch justify-center text-gray-900 text-base font-semibold font-['Poppins'] capitalize leading-10 line-clamp-1">Family & Social Background</div>
                    <div className="self-stretch flex flex-col justify-start items-start gap-5">
                        <div className="self-stretch flex flex-col justify-start items-start gap-4">
                            <div className="self-stretch inline-flex justify-start items-start gap-4">
                                <div data-property-1="Input Default" className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                                    <div className="self-stretch justify-start"><span className="text-teal-950 text-sm font-medium font-['Poppins'] leading-5">Family Mental Health History (yes/no; describe)</span><span className="text-red-600 text-sm font-medium font-['Poppins'] leading-5"> *</span></div>
                                    <input
                                      type="text"
                                      name="familyMentalHealth"
                                      value={formData.familyMentalHealth}
                                      onChange={handleInputChange}
                                      className="self-stretch h-9 px-3 py-1.5 bg-white rounded-md outline outline-1 outline-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="Yes/No and details..."
                                    />
                                </div>
                                <div data-property-1="Input Default" className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                                    <div className="self-stretch justify-start"><span className="text-teal-950 text-sm font-medium font-['Poppins'] leading-5">Marital Status / Relationship Context </span><span className="text-red-600 text-sm font-medium font-['Poppins'] leading-5">*</span></div>
                                    <input
                                      type="text"
                                      name="maritalStatus"
                                      value={formData.maritalStatus}
                                      onChange={handleInputChange}
                                      className="self-stretch h-9 px-3 py-1.5 bg-white rounded-md outline outline-1 outline-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="Enter marital status..."
                                    />
                                </div>
                            </div>
                            <div className="self-stretch inline-flex justify-center items-end gap-4">
                                <div data-property-1="Input Default" className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                                    <div className="self-stretch justify-start"><span className="text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">Current Stressors/Triggers </span><span className="text-red-600 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">*</span></div>
                                    <input
                                      type="text"
                                      name="currentStressors"
                                      value={formData.currentStressors}
                                      onChange={handleInputChange}
                                      className="self-stretch h-9 px-3 py-1.5 bg-white rounded-md outline outline-1 outline-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="Enter current stressors..."
                                    />
                                </div>
                            </div>
                            <div className="self-stretch inline-flex justify-center items-end gap-4">
                                <div data-property-1="Input Default" className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                                    <div className="self-stretch justify-start"><span className="text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">Living Situation </span><span className="text-red-600 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">*</span></div>
                                    <input
                                      type="text"
                                      name="livingSituation"
                                      value={formData.livingSituation}
                                      onChange={handleInputChange}
                                      className="self-stretch h-9 px-3 py-1.5 bg-white rounded-md outline outline-1 outline-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="Enter living situation..."
                                    />
                                </div>
                            </div>
                            <div className="self-stretch inline-flex justify-center items-end gap-4">
                                <div data-property-1="Input Default" className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                                    <div className="self-stretch justify-start"><span className="text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">Employment / Education </span><span className="text-red-600 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">*</span></div>
                                    <input
                                      type="text"
                                      name="employment"
                                      value={formData.employment}
                                      onChange={handleInputChange}
                                      className="self-stretch h-9 px-3 py-1.5 bg-white rounded-md outline outline-1 outline-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="Enter employment/education status..."
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="self-stretch flex flex-col justify-start items-start gap-1">
                            <div className="self-stretch justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">Support System Strength</div>
                            <div className="w-[1149px] inline-flex justify-start items-center gap-6">
                                <div className="flex justify-start items-center gap-2">
                                    <input
                                      type="radio"
                                      id="support-strong"
                                      name="supportSystemStrength"
                                      value="strong"
                                      checked={formData.supportSystemStrength === 'strong'}
                                      onChange={handleInputChange}
                                      className="w-4 h-4"
                                    />
                                    <label htmlFor="support-strong" className="justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide cursor-pointer">Strong</label>
                                </div>
                                <div className="flex justify-start items-center gap-2">
                                    <input
                                      type="radio"
                                      id="support-moderate"
                                      name="supportSystemStrength"
                                      value="moderate"
                                      checked={formData.supportSystemStrength === 'moderate'}
                                      onChange={handleInputChange}
                                      className="w-4 h-4"
                                    />
                                    <label htmlFor="support-moderate" className="justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide cursor-pointer">Moderate</label>
                                </div>
                                <div className="flex justify-start items-center gap-2">
                                    <input
                                      type="radio"
                                      id="support-weak"
                                      name="supportSystemStrength"
                                      value="weak"
                                      checked={formData.supportSystemStrength === 'weak'}
                                      onChange={handleInputChange}
                                      className="w-4 h-4"
                                    />
                                    <label htmlFor="support-weak" className="justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide cursor-pointer">Weak</label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Use & Risk Behaviors Substance */}
          <div className="w-[1189px] px-px pt-5 pb-px bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-slate-300 inline-flex flex-col justify-start items-start">
            <div className="self-stretch px-4 py-4 bg-gray-50/80 rounded-tl-lg rounded-tr-lg border-b border-slate-300 inline-flex justify-start items-center">
                <div className="justify-center text-teal-950 text-base font-semibold font-['Poppins'] leading-6">Use & Risk Behaviors Substance</div>
            </div>
            <div className="self-stretch p-4 flex flex-col justify-start items-start">
                <div className="self-stretch p-2.5 bg-slate-300 rounded inline-flex justify-start items-start">
                    <div className="w-60 self-stretch px-2 inline-flex flex-col justify-start items-start">
                        <div className="justify-center text-teal-950 text-sm font-semibold font-['Poppins'] leading-5">Substance</div>
                    </div>
                    <div className="w-64 self-stretch px-2 inline-flex flex-col justify-start items-start">
                        <div className="justify-center text-teal-950 text-sm font-semibold font-['Poppins'] leading-5">Frequency</div>
                    </div>
                    <div className="w-64 self-stretch px-2 inline-flex flex-col justify-start items-start">
                        <div className="justify-center text-teal-950 text-sm font-semibold font-['Poppins'] leading-5">Duration</div>
                    </div>
                    <div className="w-56 self-stretch px-2 inline-flex flex-col justify-start items-start">
                        <div className="justify-center text-teal-950 text-sm font-semibold font-['Poppins'] leading-5">Last use</div>
                    </div>
                    <div className="w-44 px-2 inline-flex flex-col justify-start items-start">
                        <div className="justify-center text-teal-950 text-sm font-semibold font-['Poppins'] leading-5">Related issues</div>
                    </div>
                </div>
                {/* Alcohol Row */}
                <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                    <div className="flex justify-start items-center gap-8">
                        <div className="w-36 px-2 flex justify-start items-center">
                            <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Alcohol</div>
                        </div>
                        <div className="w-[980px] flex justify-start items-center gap-3">
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.alcohol.frequency}
                                  onChange={(e) => handleSubstanceUseChange('alcohol', 'frequency', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., Daily"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.alcohol.duration}
                                  onChange={(e) => handleSubstanceUseChange('alcohol', 'duration', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., 5 years"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.alcohol.lastUse}
                                  onChange={(e) => handleSubstanceUseChange('alcohol', 'lastUse', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., Yesterday"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.alcohol.relatedIssues}
                                  onChange={(e) => handleSubstanceUseChange('alcohol', 'relatedIssues', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Any issues"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Cannabis Row */}
                <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                    <div className="flex justify-start items-center gap-8">
                        <div className="w-36 px-2 flex justify-start items-center">
                            <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Cannabis</div>
                        </div>
                        <div className="w-[980px] flex justify-start items-center gap-3">
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.cannabis.frequency}
                                  onChange={(e) => handleSubstanceUseChange('cannabis', 'frequency', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., Weekly"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.cannabis.duration}
                                  onChange={(e) => handleSubstanceUseChange('cannabis', 'duration', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., 2 years"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.cannabis.lastUse}
                                  onChange={(e) => handleSubstanceUseChange('cannabis', 'lastUse', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., Last week"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.cannabis.relatedIssues}
                                  onChange={(e) => handleSubstanceUseChange('cannabis', 'relatedIssues', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Any issues"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Prescription Row */}
                <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                    <div className="flex justify-start items-center gap-8">
                        <div className="w-36 px-2 flex justify-start items-center">
                            <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Prescription</div>
                        </div>
                        <div className="w-[980px] flex justify-start items-center gap-3">
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.prescription.frequency}
                                  onChange={(e) => handleSubstanceUseChange('prescription', 'frequency', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Frequency"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.prescription.duration}
                                  onChange={(e) => handleSubstanceUseChange('prescription', 'duration', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Duration"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.prescription.lastUse}
                                  onChange={(e) => handleSubstanceUseChange('prescription', 'lastUse', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Last use"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.prescription.relatedIssues}
                                  onChange={(e) => handleSubstanceUseChange('prescription', 'relatedIssues', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Any issues"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Misuse Row */}
                <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                    <div className="flex justify-start items-center gap-8">
                        <div className="w-36 px-2 flex justify-start items-center">
                            <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Misuse</div>
                        </div>
                        <div className="w-[980px] flex justify-start items-center gap-3">
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.misuse.frequency}
                                  onChange={(e) => handleSubstanceUseChange('misuse', 'frequency', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Frequency"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.misuse.duration}
                                  onChange={(e) => handleSubstanceUseChange('misuse', 'duration', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Duration"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.misuse.lastUse}
                                  onChange={(e) => handleSubstanceUseChange('misuse', 'lastUse', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Last use"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.misuse.relatedIssues}
                                  onChange={(e) => handleSubstanceUseChange('misuse', 'relatedIssues', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Any issues"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Others Row */}
                <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                    <div className="flex justify-start items-center gap-8">
                        <div className="w-36 px-2 flex justify-start items-center">
                            <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Others</div>
                        </div>
                        <div className="w-[980px] flex justify-start items-center gap-3">
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.others.frequency}
                                  onChange={(e) => handleSubstanceUseChange('others', 'frequency', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Frequency"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.others.duration}
                                  onChange={(e) => handleSubstanceUseChange('others', 'duration', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Duration"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.others.lastUse}
                                  onChange={(e) => handleSubstanceUseChange('others', 'lastUse', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Last use"
                                />
                            </div>
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.substanceUse.others.relatedIssues}
                                  onChange={(e) => handleSubstanceUseChange('others', 'relatedIssues', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Any issues"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Symptom Tracking (Quantitative)  */}
          <div className="w-[1189px] px-px pt-5 pb-px bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-slate-300 inline-flex flex-col justify-start items-start">
              <div className="self-stretch px-4 py-4 bg-gray-50/80 rounded-tl-lg rounded-tr-lg border-b border-slate-300 inline-flex justify-start items-center">
                  <div className="justify-center text-teal-950 text-base font-semibold font-['Poppins'] leading-6">Symptom Tracking (Quantitative)</div>
              </div>
              <div className="self-stretch p-4 flex flex-col justify-start items-start">
                  <div className="self-stretch p-2.5 bg-slate-300 rounded inline-flex justify-start items-start">
                      <div className="w-72 self-stretch px-2 inline-flex flex-col justify-start items-start">
                          <div className="justify-center text-teal-950 text-sm font-semibold font-['Poppins'] leading-5">Symptom</div>
                      </div>
                      <div className="w-60 self-stretch px-2 inline-flex flex-col justify-start items-start">
                          <div className="justify-center text-teal-950 text-sm font-semibold font-['Poppins'] leading-5">Rating</div>
                      </div>
                      <div className="w-56 self-stretch px-2 inline-flex flex-col justify-start items-start">
                          <div className="justify-center text-teal-950 text-sm font-semibold font-['Poppins'] leading-5">Onset</div>
                      </div>
                      <div className="w-64 self-stretch px-2 inline-flex flex-col justify-start items-start">
                          <div className="justify-center text-teal-950 text-sm font-semibold font-['Poppins'] leading-5">Frequency</div>
                      </div>
                      <div className="w-36 px-2 inline-flex flex-col justify-start items-start">
                          <div className="justify-center text-teal-950 text-sm font-semibold font-['Poppins'] leading-5">Notes</div>
                      </div>
                  </div>
                  {/* Anxiety Row */}
                  <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                      <div className="flex justify-start items-center gap-8">
                          <div className="w-48 px-2 flex justify-start items-center">
                              <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Anxiety</div>
                          </div>
                          <div className="w-[929px] flex justify-start items-center gap-3">
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={formData.symptomTracking.anxiety.rating}
                                    onChange={(e) => handleSymptomTrackingChange('anxiety', 'rating', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0-10"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.anxiety.onset}
                                    onChange={(e) => handleSymptomTrackingChange('anxiety', 'onset', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., 2 months ago"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.anxiety.frequency}
                                    onChange={(e) => handleSymptomTrackingChange('anxiety', 'frequency', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., Daily"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.anxiety.notes}
                                    onChange={(e) => handleSymptomTrackingChange('anxiety', 'notes', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Notes"
                                  />
                              </div>
                          </div>
                      </div>
                  </div>

                  {/* Depression Row */}
                  <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                      <div className="flex justify-start items-center gap-8">
                          <div className="w-48 px-2 flex justify-start items-center">
                              <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Depression</div>
                          </div>
                          <div className="w-[929px] flex justify-start items-center gap-3">
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={formData.symptomTracking.depression.rating}
                                    onChange={(e) => handleSymptomTrackingChange('depression', 'rating', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0-10"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.depression.onset}
                                    onChange={(e) => handleSymptomTrackingChange('depression', 'onset', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Onset"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.depression.frequency}
                                    onChange={(e) => handleSymptomTrackingChange('depression', 'frequency', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Frequency"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.depression.notes}
                                    onChange={(e) => handleSymptomTrackingChange('depression', 'notes', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Notes"
                                  />
                              </div>
                          </div>
                      </div>
                  </div>

                  {/* Attention/Concentration Row */}
                  <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                      <div className="flex justify-start items-center gap-8">
                          <div className="w-48 px-2 flex justify-start items-center">
                              <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Attention/Concentration</div>
                          </div>
                          <div className="w-[929px] flex justify-start items-center gap-3">
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={formData.symptomTracking.attention.rating}
                                    onChange={(e) => handleSymptomTrackingChange('attention', 'rating', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0-10"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.attention.onset}
                                    onChange={(e) => handleSymptomTrackingChange('attention', 'onset', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Onset"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.attention.frequency}
                                    onChange={(e) => handleSymptomTrackingChange('attention', 'frequency', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Frequency"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.attention.notes}
                                    onChange={(e) => handleSymptomTrackingChange('attention', 'notes', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Notes"
                                  />
                              </div>
                          </div>
                      </div>
                  </div>

                  {/* Impulsivity Row */}
                  <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                      <div className="flex justify-start items-center gap-8">
                          <div className="w-48 px-2 flex justify-start items-center">
                              <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Impulsivity</div>
                          </div>
                          <div className="w-[929px] flex justify-start items-center gap-3">
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={formData.symptomTracking.impulsivity.rating}
                                    onChange={(e) => handleSymptomTrackingChange('impulsivity', 'rating', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0-10"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.impulsivity.onset}
                                    onChange={(e) => handleSymptomTrackingChange('impulsivity', 'onset', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Onset"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.impulsivity.frequency}
                                    onChange={(e) => handleSymptomTrackingChange('impulsivity', 'frequency', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Frequency"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.impulsivity.notes}
                                    onChange={(e) => handleSymptomTrackingChange('impulsivity', 'notes', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Notes"
                                  />
                              </div>
                          </div>
                      </div>
                  </div>

                  {/* Sleep Problems Row */}
                  <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                      <div className="flex justify-start items-center gap-8">
                          <div className="w-48 px-2 flex justify-start items-center">
                              <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Sleep Problems</div>
                          </div>
                          <div className="w-[929px] flex justify-start items-center gap-3">
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={formData.symptomTracking.sleepProblems.rating}
                                    onChange={(e) => handleSymptomTrackingChange('sleepProblems', 'rating', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0-10"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.sleepProblems.onset}
                                    onChange={(e) => handleSymptomTrackingChange('sleepProblems', 'onset', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Onset"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.sleepProblems.frequency}
                                    onChange={(e) => handleSymptomTrackingChange('sleepProblems', 'frequency', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Frequency"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.sleepProblems.notes}
                                    onChange={(e) => handleSymptomTrackingChange('sleepProblems', 'notes', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Notes"
                                  />
                              </div>
                          </div>
                      </div>
                  </div>

                  {/* Irritability/Anger Row */}
                  <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                      <div className="flex justify-start items-center gap-8">
                          <div className="w-48 px-2 flex justify-start items-center">
                              <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Irritability/Anger</div>
                          </div>
                          <div className="w-[929px] flex justify-start items-center gap-3">
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={formData.symptomTracking.irritability.rating}
                                    onChange={(e) => handleSymptomTrackingChange('irritability', 'rating', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0-10"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.irritability.onset}
                                    onChange={(e) => handleSymptomTrackingChange('irritability', 'onset', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Onset"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.irritability.frequency}
                                    onChange={(e) => handleSymptomTrackingChange('irritability', 'frequency', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Frequency"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.irritability.notes}
                                    onChange={(e) => handleSymptomTrackingChange('irritability', 'notes', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Notes"
                                  />
                              </div>
                          </div>
                      </div>
                  </div>

                  {/* Suicidal Ideation Row */}
                  <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                      <div className="flex justify-start items-center gap-8">
                          <div className="w-48 px-2 flex justify-start items-center">
                              <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Suicidal Ideation</div>
                          </div>
                          <div className="w-[929px] flex justify-start items-center gap-3">
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={formData.symptomTracking.suicidalIdeation.rating}
                                    onChange={(e) => handleSymptomTrackingChange('suicidalIdeation', 'rating', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0-10"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.suicidalIdeation.onset}
                                    onChange={(e) => handleSymptomTrackingChange('suicidalIdeation', 'onset', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Onset"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.suicidalIdeation.frequency}
                                    onChange={(e) => handleSymptomTrackingChange('suicidalIdeation', 'frequency', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Frequency"
                                  />
                              </div>
                              <div className="flex-1 inline-flex flex-col justify-center items-center">
                                  <input
                                    type="text"
                                    value={formData.symptomTracking.suicidalIdeation.notes}
                                    onChange={(e) => handleSymptomTrackingChange('suicidalIdeation', 'notes', e.target.value)}
                                    className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Notes"
                                  />
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>

          {/* Functional Impact */}
          <div className="w-[1188px] rounded-lg outline outline-1 outline-offset-[-1px] outline-slate-300 inline-flex flex-col justify-center items-center gap-2.5">
            <div className="w-[1164px] flex flex-col justify-start items-start gap-2.5 p-5">
                <div className="w-[1164px] flex flex-col justify-start items-start gap-3">
                    <div className="self-stretch justify-center text-gray-900 text-base font-semibold font-['Poppins'] capitalize leading-10 line-clamp-1">Functional Impact</div>
                    <div className="self-stretch flex flex-col justify-start items-start gap-4">
                        {/* Daily Functioning - Radio Buttons */}
                        <div className="self-stretch flex flex-col justify-start items-start gap-3">
                            <div className="self-stretch justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">Daily Functioning</div>
                            <div className="w-[551px] inline-flex justify-start items-center gap-6">
                                <div className="flex justify-start items-center gap-2">
                                    <input
                                      type="radio"
                                      id="daily-mild"
                                      name="dailyFunctioning"
                                      value="mild"
                                      checked={formData.dailyFunctioning === 'mild'}
                                      onChange={handleInputChange}
                                      className="w-4 h-4"
                                    />
                                    <label htmlFor="daily-mild" className="justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide cursor-pointer">Mild</label>
                                </div>
                                <div className="flex justify-start items-center gap-2">
                                    <input
                                      type="radio"
                                      id="daily-moderate"
                                      name="dailyFunctioning"
                                      value="moderate"
                                      checked={formData.dailyFunctioning === 'moderate'}
                                      onChange={handleInputChange}
                                      className="w-4 h-4"
                                    />
                                    <label htmlFor="daily-moderate" className="justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide cursor-pointer">Moderate</label>
                                </div>
                                <div className="flex justify-start items-center gap-2">
                                    <input
                                      type="radio"
                                      id="daily-severe"
                                      name="dailyFunctioning"
                                      value="severe"
                                      checked={formData.dailyFunctioning === 'severe'}
                                      onChange={handleInputChange}
                                      className="w-4 h-4"
                                    />
                                    <label htmlFor="daily-severe" className="justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide cursor-pointer">Severe</label>
                                </div>
                            </div>
                        </div>

                        {/* Work/School Performance */}
                        <div className="self-stretch inline-flex justify-center items-end gap-4">
                            <div data-property-1="Input Default" className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                                <div className="self-stretch justify-start"><span className="text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">Work/School Performance </span><span className="text-red-600 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">*</span></div>
                                <input
                                  type="text"
                                  name="workSchoolPerformance"
                                  value={formData.workSchoolPerformance}
                                  onChange={handleInputChange}
                                  className="self-stretch h-9 px-3 py-1.5 bg-white rounded-md outline outline-1 outline-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Describe work/school performance..."
                                />
                            </div>
                        </div>

                        {/* Social Relationships */}
                        <div className="self-stretch inline-flex justify-center items-end gap-4">
                            <div data-property-1="Input Default" className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                                <div className="self-stretch justify-start"><span className="text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">Social Relationships </span><span className="text-red-600 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">*</span></div>
                                <input
                                  type="text"
                                  name="socialRelationships"
                                  value={formData.socialRelationships}
                                  onChange={handleInputChange}
                                  className="self-stretch h-9 px-3 py-1.5 bg-white rounded-md outline outline-1 outline-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Describe social relationships..."
                                />
                            </div>
                        </div>

                        {/* Self-Care/Motivation */}
                        <div className="self-stretch inline-flex justify-center items-end gap-4">
                            <div data-property-1="Input Default" className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                                <div className="self-stretch justify-start"><span className="text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">Self-Care/Motivation </span><span className="text-red-600 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">*</span></div>
                                <input
                                  type="text"
                                  name="selfCareMotivation"
                                  value={formData.selfCareMotivation}
                                  onChange={handleInputChange}
                                  className="self-stretch h-9 px-3 py-1.5 bg-white rounded-md outline outline-1 outline-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Describe self-care and motivation..."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Mental Status Exam */}
          <div className="w-[1189px] px-px pt-5 pb-px bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-slate-300 inline-flex flex-col justify-start items-start">
            <div className="self-stretch px-4 py-4 bg-gray-50/80 rounded-tl-lg rounded-tr-lg border-b border-slate-300 inline-flex justify-start items-center">
                <div className="justify-center text-teal-950 text-base font-semibold font-['Poppins'] leading-6">Mental Status Exam</div>
            </div>
            <div className="self-stretch p-4 flex flex-col justify-start items-start">
                <div className="self-stretch p-2.5 bg-slate-300 rounded inline-flex justify-start items-start">
                    <div className="w-[594px] self-stretch px-2 inline-flex flex-col justify-start items-start">
                        <div className="justify-center text-teal-950 text-sm font-semibold font-['Poppins'] leading-5">Domain</div>
                    </div>
                    <div className="flex-1 self-stretch px-2 inline-flex flex-col justify-start items-start">
                        <div className="justify-center text-teal-950 text-sm font-semibold font-['Poppins'] leading-5">Observation</div>
                    </div>
                </div>

                {/* Appearance Row */}
                <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                    <div className="flex justify-start items-center gap-8">
                        <div className="w-48 px-2 flex justify-start items-center">
                            <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Appearance</div>
                        </div>
                        <div className="w-[929px] flex justify-start items-center gap-3">
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.mentalStatusExam.appearance}
                                  onChange={(e) => handleMentalStatusExamChange('appearance', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Describe appearance..."
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Speech Row */}
                <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                    <div className="flex justify-start items-center gap-8">
                        <div className="w-48 px-2 flex justify-start items-center">
                            <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Speech</div>
                        </div>
                        <div className="w-[929px] flex justify-start items-center gap-3">
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.mentalStatusExam.speech}
                                  onChange={(e) => handleMentalStatusExamChange('speech', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Describe speech..."
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Affect Row */}
                <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                    <div className="flex justify-start items-center gap-8">
                        <div className="w-48 px-2 flex justify-start items-center">
                            <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Affect</div>
                        </div>
                        <div className="w-[929px] flex justify-start items-center gap-3">
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.mentalStatusExam.affect}
                                  onChange={(e) => handleMentalStatusExamChange('affect', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Describe affect..."
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Thought Process Row */}
                <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                    <div className="flex justify-start items-center gap-8">
                        <div className="w-48 px-2 flex justify-start items-center">
                            <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Thought Process</div>
                        </div>
                        <div className="w-[929px] flex justify-start items-center gap-3">
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.mentalStatusExam.thoughtProcess}
                                  onChange={(e) => handleMentalStatusExamChange('thoughtProcess', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Describe thought process..."
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Thought Content Row */}
                <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                    <div className="flex justify-start items-center gap-8">
                        <div className="w-48 px-2 flex justify-start items-center">
                            <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Thought Content</div>
                        </div>
                        <div className="w-[929px] flex justify-start items-center gap-3">
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.mentalStatusExam.thoughtContent}
                                  onChange={(e) => handleMentalStatusExamChange('thoughtContent', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Describe thought content..."
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Insight / Judgment Row */}
                <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                    <div className="flex justify-start items-center gap-8">
                        <div className="w-48 px-2 flex justify-start items-center">
                            <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Insight / Judgment</div>
                        </div>
                        <div className="w-[929px] flex justify-start items-center gap-3">
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.mentalStatusExam.insightJudgment}
                                  onChange={(e) => handleMentalStatusExamChange('insightJudgment', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Describe insight and judgment..."
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Orientation Row */}
                <div className="self-stretch py-3 border-b border-slate-300 inline-flex justify-start items-center">
                    <div className="flex justify-start items-center gap-8">
                        <div className="w-48 px-2 flex justify-start items-center">
                            <div className="justify-center text-neutral-700 text-sm font-semibold font-['Poppins'] leading-5">Orientation</div>
                        </div>
                        <div className="w-[929px] flex justify-start items-center gap-3">
                            <div className="flex-1 inline-flex flex-col justify-center items-center">
                                <input
                                  type="text"
                                  value={formData.mentalStatusExam.orientation}
                                  onChange={(e) => handleMentalStatusExamChange('orientation', e.target.value)}
                                  className="self-stretch h-9 px-3 py-2 bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Describe orientation..."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Research Consent & Data Use */}
          <div className="w-[1188px] rounded-lg outline outline-1 outline-offset-[-1px] outline-slate-300 inline-flex flex-col justify-center items-center gap-2.5">
            <div className="w-[1164px] flex flex-col justify-start items-start gap-2.5 p-5">
                <div className="w-[1164px] flex flex-col justify-start items-start gap-3">
                    <div className="self-stretch justify-center text-gray-900 text-base font-semibold font-['Poppins'] capitalize leading-10 line-clamp-1">Research Consent & Data Use</div>

                    {/* Consent to anonymized data use */}
                    <div className="self-stretch flex flex-col justify-start items-start gap-2.5">
                        <div className="self-stretch flex flex-col justify-start items-start gap-5">
                            <div className="self-stretch inline-flex justify-start items-start gap-1">
                                <div className="w-[480px] justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">Consent to anonymized data use for AI & research module</div>
                                <div className="flex justify-start items-center gap-6">
                                    <div className="flex justify-start items-center gap-2">
                                        <input
                                          type="radio"
                                          id="data-use-yes"
                                          name="dataUseConsent"
                                          value="yes"
                                          checked={formData.dataUseConsent === 'yes'}
                                          onChange={handleInputChange}
                                          className="w-4 h-4"
                                        />
                                        <label htmlFor="data-use-yes" className="justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide cursor-pointer">Yes</label>
                                    </div>
                                    <div className="flex justify-start items-center gap-2">
                                        <input
                                          type="radio"
                                          id="data-use-no"
                                          name="dataUseConsent"
                                          value="no"
                                          checked={formData.dataUseConsent === 'no'}
                                          onChange={handleInputChange}
                                          className="w-4 h-4"
                                        />
                                        <label htmlFor="data-use-no" className="justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide cursor-pointer">No</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Consent for behavioral analytics tracking */}
                    <div className="self-stretch flex flex-col justify-start items-start gap-2.5">
                        <div className="self-stretch flex flex-col justify-start items-start gap-5">
                            <div className="self-stretch inline-flex justify-start items-start gap-1">
                                <div className="w-[480px] justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide">Consent for behavioral analytics tracking (non-identifiable)</div>
                                <div className="flex justify-start items-center gap-6">
                                    <div className="flex justify-start items-center gap-2">
                                        <input
                                          type="radio"
                                          id="analytics-yes"
                                          name="analyticsConsent"
                                          value="yes"
                                          checked={formData.analyticsConsent === 'yes'}
                                          onChange={handleInputChange}
                                          className="w-4 h-4"
                                        />
                                        <label htmlFor="analytics-yes" className="justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide cursor-pointer">Yes</label>
                                    </div>
                                    <div className="flex justify-start items-center gap-2">
                                        <input
                                          type="radio"
                                          id="analytics-no"
                                          name="analyticsConsent"
                                          value="no"
                                          checked={formData.analyticsConsent === 'no'}
                                          onChange={handleInputChange}
                                          className="w-4 h-4"
                                        />
                                        <label htmlFor="analytics-no" className="justify-start text-teal-950 text-sm font-medium font-['Poppins'] leading-6 tracking-wide cursor-pointer">No</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>

             {/* Clinician Summary */}
          <div className="w-full p-5 bg-white rounded-lg border border-[#C4CADA] flex flex-col gap-5">
            <div className="text-[#111827] text-base font-semibold font-['Poppins'] capitalize leading-10">
              Clinician Summary
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                Preliminary Impression <span className="text-[#FF0000]">*</span>
              </label>
              <textarea
                name="preliminaryImpression"
                value={formData.preliminaryImpression}
                onChange={handleInputChange}
                rows="5"
                className="px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                Next Steps <span className="text-[#FF0000]">*</span>
              </label>
              <textarea
                name="nextSteps"
                value={formData.nextSteps}
                onChange={handleInputChange}
                rows="5"
                className="px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                Follow-Up Plan <span className="text-[#FF0000]">*</span>
              </label>
              <textarea
                name="followUpPlan"
                value={formData.followUpPlan}
                onChange={handleInputChange}
                rows="5"
                className="px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>

          {/* Custom Fields */}
          <div className="w-full p-5 bg-white rounded-lg border border-[#C4CADA] flex flex-col gap-5">
            <div className="flex justify-between items-center">
              <div className="text-[#111827] text-base font-semibold font-['Poppins'] capitalize leading-10">
                Custom fields
              </div>
              <button
                type="button"
                onClick={handleAddFieldClick}
                className="px-4 py-2 bg-[#1E88E5] text-white rounded-md text-sm font-medium font-['Poppins'] hover:bg-blue-600 transition-colors"
              >
                + Add Field
              </button>
            </div>

            {/* Display Custom Fields */}
            {customFields.length > 0 && (
              <div className="flex flex-col gap-4">
                {customFields.map((field) => (
                  <div key={field.id} className="flex gap-2 items-start">
                    <div className="flex-1 flex flex-col gap-1">
                      <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                        {field.title}
                      </label>
                      {field.type === 'text' ? (
                        <input
                          type="text"
                          id={field.id}
                          name={field.id}
                          value={formData[field.id] || ''}
                          onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                          placeholder={field.placeholder}
                          className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <textarea
                          id={field.id}
                          name={field.id}
                          value={formData[field.id] || ''}
                          onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                          placeholder={field.placeholder}
                          rows="3"
                          className="px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveField(field.id)}
                      className="mt-7 text-red-500 hover:text-red-700 text-xl font-bold"
                      title="Remove field"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {customFields.length === 0 && (
              <div className="text-center text-[#6B7280] text-sm font-normal font-['Poppins'] py-4">
                No custom fields added yet. Click "Add Field" to create one.
              </div>
            )}
          </div>

          {/* Add Field Modal */}
          {showFieldModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-[#001C3B] text-lg font-semibold font-['Poppins']">
                    Add Custom Field
                  </h3>
                  <button
                    onClick={() => setShowFieldModal(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>

                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-[#001C3B] text-sm font-medium font-['Poppins']">
                      Field Type <span className="text-[#FF0000]">*</span>
                    </label>
                    <select
                      name="type"
                      value={newField.type}
                      onChange={handleFieldModalChange}
                      className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="text">Text Input</option>
                      <option value="textarea">Text Area</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-[#001C3B] text-sm font-medium font-['Poppins']">
                      Field Title <span className="text-[#FF0000]">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={newField.title}
                      onChange={handleFieldModalChange}
                      placeholder="Enter field title"
                      className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-[#001C3B] text-sm font-medium font-['Poppins']">
                      Field ID <span className="text-[#FF0000]">*</span>
                    </label>
                    <input
                      type="text"
                      name="id"
                      value={newField.id}
                      onChange={handleFieldModalChange}
                      placeholder="e.g., customField1"
                      className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-500">Unique identifier for this field</span>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-[#001C3B] text-sm font-medium font-['Poppins']">
                      Placeholder
                    </label>
                    <input
                      type="text"
                      name="placeholder"
                      value={newField.placeholder}
                      onChange={handleFieldModalChange}
                      placeholder="Enter placeholder text"
                      className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => setShowFieldModal(false)}
                      className="flex-1 h-10 bg-white rounded-lg border border-[#001C3B] text-[#001C3B] text-sm font-medium font-['Poppins'] hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddField}
                      className="flex-1 h-10 bg-[#1E88E5] rounded-lg text-white text-sm font-medium font-['Poppins'] hover:bg-blue-600 transition-colors"
                    >
                      Add Field
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {saveError && (
            <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm font-medium font-['Poppins']">
                {saveError}
              </p>
            </div>
          )}

          {/* Action Buttons - Step 1 */}
          <div className="w-full flex justify-end items-center gap-3 pb-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-[93px] h-12 bg-white rounded-lg border border-[#001C3B] text-[#001C3B] text-base font-medium font-['Poppins'] leading-5 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.fullName}
              className="w-[135px] h-12 bg-[#1E88E5] rounded-lg border border-[#E6E6E6] text-white text-base font-medium font-['Poppins'] leading-5 hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          </form>
          ) : currentStep === 2 ? (
            // STEP 2: Prompt Section
            <>
              {/* Prompt Editor Section */}
              <div className="w-full bg-white rounded-lg border border-[#C4CADA] flex flex-col p-5">
                <div className="w-full mb-4">
                  <label className="text-[#111827] text-base font-semibold font-['Poppins'] leading-10 mb-2 block">
                    Enter Prompt Here
                  </label>
                  <div className="prompt-editor-container">
                    <ReactQuill
                      theme="snow"
                      value={prompt}
                      onChange={setPrompt}
                      modules={modules}
                      formats={formats}
                      placeholder="Enter your prompt here..."
                      className="bg-white"
                      style={{
                        height: '400px',
                        marginBottom: '50px'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {saveError && (
                <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm font-medium font-['Poppins']">
                    {saveError}
                  </p>
                </div>
              )}

              {/* Action Buttons - Step 2 */}
              <div className="w-full flex justify-between items-center pb-6">
                <button
                  type="button"
                  onClick={handleBackStep}
                  className="w-[93px] h-12 bg-white rounded-lg border border-[#001C3B] text-[#001C3B] text-base font-medium font-['Poppins'] leading-5 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleSaveToTemplates}
                    className="h-12 px-6 bg-white rounded-lg border border-[#001C3B] text-[#001C3B] text-base font-medium font-['Poppins'] leading-5 hover:bg-gray-50 transition-colors"
                  >
                    Save to templates
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSaving}
                    className="w-[135px] h-12 bg-[#1E88E5] rounded-lg border border-[#E6E6E6] text-white text-base font-medium font-['Poppins'] leading-5 hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      'Next'
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </Layout>
  );
};

export default AddPatient;
